import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiCloudArrowUp,
  HiChevronDown,
  HiChevronUp,
  HiDocumentDuplicate,
  HiLink,
  HiGlobeAlt,
  HiXMark,
} from 'react-icons/hi2';

const MAX_PEOPLE_VISIBLE = 5;
const NOTES_MAX_LENGTH = 500;

/** Labels map to normalized names via roleResolver.normalizeRoleName (Admin, Coach, SuperAdmin, …). */
export const DOCUMENT_BANK_VISIBILITY_ROLE_LABELS = [
  'Super Admin',
  'Admin',
  'Moderator',
  'Business Advisor',
  'Coach',
  'Alumni',
  'Client',
  'Developer',
];

export default function UploadPanel({
  canUpload,
  categories,
  selectedCategory,
  setSelectedCategory,
  selectedFile,
  setSelectedFile,
  notes,
  setNotes,
  uploading,
  onUpload,
  ACCEPTED_TYPES,
  visibilityType,
  setVisibilityType,
  visibilityRoles,
  setVisibilityRoles,
  people,
  visibilityUsers,
  setVisibilityUsers,
  formatFileSize,
  linkUrl,
  setLinkUrl,
  linkName,
  setLinkName,
  uploadType,
  setUploadType,
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragError, setDragError] = useState('');
  const [peopleExpanded, setPeopleExpanded] = useState(false);

  const handleUploadTypeChange = type => {
    setUploadType(type);
    setSelectedFile(null);
    setLinkUrl('');
    setLinkName('');
    setDragError('');
  };

  const handleDragEnter = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setDragError('');
  }, []);

  const handleDragLeave = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        const file = files[0];
        if (ACCEPTED_TYPES.includes(file.type)) {
          setSelectedFile(file);
          setDragError('');
        } else {
          setDragError('Invalid file type. Please upload PDF, Word, Excel, or image files.');
        }
      }
    },
    [ACCEPTED_TYPES, setSelectedFile]
  );

  const peopleToShow = peopleExpanded ? people : people.slice(0, MAX_PEOPLE_VISIBLE);
  const hasMorePeople = people.length > MAX_PEOPLE_VISIBLE;

  if (!canUpload) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="min-w-0 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg shadow-gray-200/50 ring-1 ring-gray-100"
    >
      {/* Header */}
      <div className="border-b border-gray-100 bg-gradient-to-r from-blue-50/80 to-white px-4 py-5 sm:px-6 lg:px-8">
        <div className="flex items-center gap-4">
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/25"
          >
            <HiCloudArrowUp className="h-6 w-6" />
          </motion.div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 sm:text-xl">Upload Document</h3>
            <p className="mt-0.5 text-xs text-gray-500 sm:text-sm">
              Add files or links to your repository
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 lg:p-8">
        {/* Upload Type Toggle */}
        <div className="mb-6 flex gap-2 rounded-xl border border-gray-200 bg-gray-50/50 p-1.5 shadow-inner">
          <button
            type="button"
            onClick={() => handleUploadTypeChange('file')}
            className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold transition-all sm:px-4 ${
              uploadType === 'file'
                ? 'bg-white text-blue-600 shadow-md shadow-blue-500/10 ring-1 ring-blue-100'
                : 'text-gray-600 hover:bg-white/50 hover:text-gray-900'
            }`}
          >
            <HiCloudArrowUp className="h-4 w-4" />
            <span className="hidden sm:inline">Upload File</span>
            <span className="sm:hidden">File</span>
          </button>
          <button
            type="button"
            onClick={() => handleUploadTypeChange('link')}
            className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold transition-all sm:px-4 ${
              uploadType === 'link'
                ? 'bg-white text-blue-600 shadow-md shadow-blue-500/10 ring-1 ring-blue-100'
                : 'text-gray-600 hover:bg-white/50 hover:text-gray-900'
            }`}
          >
            <HiLink className="h-4 w-4" />
            <span className="hidden sm:inline">Add Link</span>
            <span className="sm:hidden">Link</span>
          </button>
        </div>

        {uploadType === 'file' ? (
          <div
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className={`group relative mb-6 overflow-hidden rounded-2xl border-2 border-dashed p-6 text-center transition-all sm:p-8 ${
              isDragging
                ? 'border-blue-500 bg-blue-50 shadow-lg ring-4 ring-blue-500/20'
                : dragError
                  ? 'border-red-300 bg-red-50'
                  : 'border-gray-300 bg-gradient-to-br from-white to-gray-50 hover:border-blue-400 hover:bg-gradient-to-br hover:from-blue-50 hover:to-blue-100/50 hover:shadow-lg'
            }`}
          >
            <AnimatePresence>
              {isDragging && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-blue-600/10"
                />
              )}
            </AnimatePresence>

            <motion.div
              animate={isDragging ? { scale: 1.05 } : { scale: 1 }}
              transition={{ type: 'spring', stiffness: 300 }}
              className="relative z-10"
            >
              <HiCloudArrowUp
                className={`mx-auto mb-3 h-12 w-12 transition-colors sm:mb-4 sm:h-14 sm:w-14 ${
                  isDragging ? 'text-blue-600' : dragError ? 'text-red-500' : 'text-blue-500'
                }`}
              />
              <p
                className={`mb-1 text-sm font-semibold sm:text-base ${dragError ? 'text-red-800' : 'text-gray-900'}`}
              >
                {isDragging ? 'Drop file here!' : dragError || 'Drag & drop or click to browse'}
              </p>
              <p className="mb-5 text-xs text-gray-600 sm:text-sm">
                PDF, Word, Excel, PNG, JPG, GIF
              </p>

              <input
                type="file"
                accept={ACCEPTED_TYPES.join(',')}
                onChange={e => {
                  const file = e.target.files?.[0] || null;
                  setSelectedFile(file);
                  setDragError('');
                }}
                className="hidden"
                id="upload-input"
              />
              <motion.label
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                htmlFor="upload-input"
                className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 transition-all hover:bg-blue-700 hover:shadow-xl active:scale-95 sm:px-6 sm:py-3"
              >
                <HiCloudArrowUp className="h-5 w-5" />
                Choose File
              </motion.label>
            </motion.div>

            <AnimatePresence>
              {selectedFile && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="relative z-10 mt-5 rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50 to-white p-4 text-left shadow-sm"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-blue-100 sm:h-12 sm:w-12">
                      <HiDocumentDuplicate className="h-5 w-5 text-blue-600 sm:h-6 sm:w-6" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-gray-900">
                        {selectedFile.name}
                      </p>
                      <p className="text-xs font-medium text-gray-600">
                        {formatFileSize(selectedFile.size)}
                      </p>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setSelectedFile(null)}
                      className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600"
                      aria-label="Remove file"
                    >
                      <HiXMark className="h-5 w-5" />
                    </motion.button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ) : (
          <div className="mb-6 space-y-4">
            <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-3 sm:p-4">
              <div className="flex items-center gap-2">
                <HiGlobeAlt className="h-4 w-4 flex-shrink-0 text-gray-500" />
                <p className="text-xs font-medium text-gray-700 sm:text-sm">
                  Google Drive & GitHub links supported
                </p>
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-gray-700">
                Link URL <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                value={linkUrl}
                onChange={e => setLinkUrl(e.target.value)}
                placeholder="https://drive.google.com/... or https://github.com/..."
                className="w-full rounded-xl border-2 border-gray-300 px-4 py-3 text-sm shadow-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-gray-700">
                Document Name (optional)
              </label>
              <input
                type="text"
                value={linkName}
                onChange={e => setLinkName(e.target.value)}
                placeholder="e.g., Project Documentation"
                className="w-full rounded-xl border-2 border-gray-300 px-4 py-3 text-sm shadow-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
            </div>

            {linkUrl && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="overflow-hidden rounded-xl border border-blue-200 bg-blue-50/80 p-4"
              >
                <div className="flex items-start gap-3">
                  <HiLink className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-blue-900">
                      {linkName || 'Link Document'}
                    </p>
                    <p className="mt-1 truncate text-xs text-blue-700">{linkUrl}</p>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        )}

        {/* Category & Notes */}
        <div className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-700">
              Category <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                value={selectedCategory}
                onChange={e => setSelectedCategory(e.target.value)}
                className="w-full appearance-none rounded-xl border-2 border-gray-300 bg-white px-4 py-3 pr-10 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                required
              >
                <option value="">Select a category</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
              <HiChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="text-sm font-semibold text-gray-700">Notes (optional)</label>
              <span className="text-xs text-gray-500">
                {notes.length}/{NOTES_MAX_LENGTH}
              </span>
            </div>
            <textarea
              rows={3}
              maxLength={NOTES_MAX_LENGTH}
              placeholder="Add any notes or description for this document..."
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className="w-full rounded-xl border-2 border-gray-300 px-4 py-3 text-sm shadow-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            />
          </div>

          {/* Document Scope */}
          <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-4">
            <label className="mb-3 block text-sm font-semibold text-gray-700">Document Scope</label>
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2 sm:grid sm:grid-cols-2 sm:gap-3 lg:grid-cols-5">
                {['private', 'public', 'roles', 'users', 'mixed'].map(t => (
                  <label
                    key={t}
                    className="flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 bg-white p-3 text-sm font-medium shadow-sm transition-all hover:border-blue-200 hover:shadow-md has-[:checked]:border-blue-300 has-[:checked]:bg-blue-50 has-[:checked]:ring-1 has-[:checked]:ring-blue-200"
                  >
                    <input
                      type="radio"
                      name="visibility-type"
                      checked={visibilityType === t}
                      onChange={() => setVisibilityType(t)}
                      className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="capitalize">{t}</span>
                  </label>
                ))}
              </div>
              {(visibilityType === 'roles' || visibilityType === 'mixed') && (
                <div className="mt-4">
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-600">
                    Allowed roles
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {DOCUMENT_BANK_VISIBILITY_ROLE_LABELS.map(r => {
                      const active = visibilityRoles.includes(r);
                      return (
                        <button
                          key={r}
                          type="button"
                          onClick={() =>
                            setVisibilityRoles(prev =>
                              prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]
                            )
                          }
                          className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-all ${
                            active
                              ? 'bg-blue-600 text-white shadow-md'
                              : 'border-2 border-gray-300 bg-white text-gray-700 hover:border-blue-300 hover:bg-blue-50'
                          }`}
                        >
                          {r}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {(visibilityType === 'users' || visibilityType === 'mixed') && (
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-600">
                    Allowed users
                  </label>
                  <div className="max-h-40 overflow-y-auto rounded-xl border border-gray-200 bg-white">
                    {peopleToShow.map(p => {
                      const active = visibilityUsers.some(u => u.email === p.email);
                      return (
                        <label
                          key={p.email}
                          className="flex cursor-pointer items-center justify-between gap-3 border-b border-gray-100 p-3 last:border-b-0 hover:bg-gray-50"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-medium text-gray-900">
                              {p.name || p.email}
                            </div>
                            <div className="truncate text-xs text-gray-500">{p.email}</div>
                          </div>
                          <input
                            type="checkbox"
                            checked={active}
                            onChange={e =>
                              setVisibilityUsers(prev => {
                                if (e.target.checked)
                                  return [
                                    ...prev,
                                    { uid: p.uid || null, email: p.email, name: p.name },
                                  ];
                                return prev.filter(u => u.email !== p.email);
                              })
                            }
                            className="h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </label>
                      );
                    })}
                  </div>
                  {hasMorePeople && (
                    <button
                      type="button"
                      onClick={() => setPeopleExpanded(!peopleExpanded)}
                      className="mt-2 flex w-full items-center justify-center gap-1 rounded-lg py-2 text-sm font-semibold text-blue-600 hover:bg-blue-50"
                    >
                      {peopleExpanded ? (
                        <>
                          <HiChevronUp className="h-4 w-4" /> Show less
                        </>
                      ) : (
                        <>
                          <HiChevronDown className="h-4 w-4" /> Show all {people.length} people
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Upload Button */}
          <motion.button
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            onClick={() => onUpload({ uploadType, linkUrl, linkName })}
            disabled={
              uploading ||
              !selectedCategory ||
              (uploadType === 'file' && !selectedFile) ||
              (uploadType === 'link' && !linkUrl.trim())
            }
            className="group relative w-full overflow-hidden rounded-xl bg-blue-600 px-6 py-4 text-base font-semibold text-white shadow-lg shadow-blue-500/30 transition-all duration-200 hover:bg-blue-700 hover:shadow-xl disabled:cursor-not-allowed disabled:bg-gray-400 disabled:opacity-60 disabled:shadow-none disabled:hover:bg-gray-400 sm:px-8"
          >
            <motion.span
              initial={{ x: '-100%' }}
              whileHover={{ x: '100%' }}
              transition={{ duration: 0.6, ease: 'easeInOut' }}
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent group-disabled:hidden"
            />

            <AnimatePresence mode="wait">
              {uploading ? (
                <motion.span
                  key="uploading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="relative flex items-center justify-center gap-3"
                >
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="h-5 w-5 rounded-full border-[3px] border-white/30 border-t-white"
                  />
                  <span>{uploadType === 'file' ? 'Uploading...' : 'Adding Link...'}</span>
                </motion.span>
              ) : uploadType === 'file' ? (
                <motion.span
                  key="upload"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="relative flex items-center justify-center gap-2"
                >
                  <HiCloudArrowUp className="h-5 w-5" />
                  <span>Upload Document</span>
                </motion.span>
              ) : (
                <motion.span
                  key="link"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="relative flex items-center justify-center gap-2"
                >
                  <HiLink className="h-5 w-5" />
                  <span>Add Link</span>
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
}
